import React from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { ReduxControls } from 'rhombus-react';
import { Field, getFormValues } from "redux-form";

import { inRange, requiredNotNull } from "widgets/forms/validators/validators";
import { noop } from "utils/common";
import {
	IMPOSIUM_COMPOSITION_CONSTRAINTS,
	IMPOSIUM_COMPOSITION_DIMENSION_OPTIONS,
	IMPOSIUM_COMPOSITION_DURATION_OPTIONS,
	IMPOSIUM_COMPOSITION_FRAMERATE_OPTIONS
} from "utils/constants";


import "./CompositionFields.css";

export function compositionFormValuesToPostData(compositionName, compositionValues) {
	let compositionPostData = {
		name: compositionName || null,
		width: compositionValues?.dimension?.width || null,
		height: compositionValues?.dimension?.height || null,
		duration: compositionValues?.duration?.value || null,
		frameRate: compositionValues?.frameRate?.value || null,
	};

	if (compositionValues?.duration?.value === 'custom') {
		compositionPostData.duration = compositionValues?.customDuration || null;
	}
	if (compositionValues?.dimension === 'custom') {
		compositionPostData.width = compositionValues?.customDimension?.width || null;
		compositionPostData.height = compositionValues?.customDimension?.height || null;
	}
	return compositionPostData;
}


const customDimensionRange = inRange(IMPOSIUM_COMPOSITION_CONSTRAINTS.dimensions);
const customDurationRange = inRange(IMPOSIUM_COMPOSITION_CONSTRAINTS.duration);

export const IMPOSIUM_COMPOSITION_DEFAULT_FORM_VALUES = {
	dimension: { width: 1920, height: 1080 },
	duration: { label: "15 seconds", value: 15 },
	customDuration: 30,
	frameRate: { label: "24 fps", value: 24 },
};

const normalizeDimension = (value) => {
	if (value === "custom") {
		return value;
	}

	const [width, height] = value.split("x");
	return { width: Number(width), height: Number(height) };
};


const formatDimension = (value) => {
	if (value === "custom") {
		return value;
	}

	return `${value?.width}x${value?.height}`;
};

export default function CompositionFields(props){
	const { formName, creativeIndex, name } = props;
	const formValues = useSelector(getFormValues(formName)) || {};

	//this sub form can be at the top level for default composition, or within an array of creative fields
	let compositionSubForm = formValues?.[name] || {};
	if (typeof creativeIndex === 'number') {
		compositionSubForm = formValues?.creatives?.[creativeIndex] || {};
	}

	const { dimension, customDimension, duration, customDuration } = compositionSubForm;
	const isCustomDimension = dimension === "custom";
	const isCustomDuration = duration?.value === "custom";

	// Check if dimensions is pristine or not to determine whether to update the field's required asterik
	const isDimensionsValid = isCustomDimension ? !!customDimension?.width && !!customDimension?.height : !!dimension;
	const isDurationValid = isCustomDuration ? !!customDuration : !!duration;

	return (
		<div className="composition-fields">
			<div className="item-row">
				<div className="dimension-options">
					<label
						htmlFor={`${name}.dimension-options`}
						className={classNames({
							"dimension-label": true,
							required: true,
							pristine: !isDimensionsValid,
							valid: isDimensionsValid,
						})}>
						Video Dimensions
					</label>
					{IMPOSIUM_COMPOSITION_DIMENSION_OPTIONS.map((option) => {
						const { value, label } = option;

						return (
							<Field
								key={value}
								name={`${name}.dimension`}
								className={`cypress-imposium-dimension-field-${value}`}
								label={label}
								value={value}
								required
								normalize={normalizeDimension}
								format={formatDimension}
								useValue
								component={ReduxControls.Checkbox}
								type="radio"
							/>
						);
					})}
					{isCustomDimension && <>
						<div className="custom-dimensions">
							<Field
								name={`${name}.customDimension.width`}
								className="cypress-imposium-custom-dimension-width-field"
								type="number"
								placeholder="Width"
								required={isCustomDimension}
								validate={isCustomDimension ?[requiredNotNull, customDimensionRange] : undefined}
								normalize={value => Number(value)}
								component={ReduxControls.Input}
								{...IMPOSIUM_COMPOSITION_CONSTRAINTS.dimensions}
							/>
							<span>x</span>
							<Field
								name={`${name}.customDimension.height`}
								className="cypress-imposium-custom-dimension-height-field"
								type="number"
								placeholder="Height"
								required={isCustomDimension}
								validate={isCustomDimension ?[requiredNotNull, customDimensionRange] : undefined}
								normalize={value => Number(value)}
								component={ReduxControls.Input}
								{...IMPOSIUM_COMPOSITION_CONSTRAINTS.dimensions}
							/>
						</div>
					</>
					}
				</div>
				<div className="item-column">
					<div className="duration-options">
						<Field
							name={`${name}.duration`}
							label="Duration"
							className={classNames({
								"duration-field cypress-imposium-duration-field": true,
								pristine: !isDurationValid,
							})}
							isClearable={false}
							required
							validate={requiredNotNull}
							component={ReduxControls.Select}
							options={IMPOSIUM_COMPOSITION_DURATION_OPTIONS}
							onMenuScrollToBottom={noop}
						/>

						{isCustomDuration && <>
							<Field
								name={`${name}.customDuration`}
								className="custom-duration cypress-imposium-custom-duration-field"
								type="number"
								normalize={value => Number(value)}
								required={isCustomDuration}
								validate={isCustomDimension ?[requiredNotNull, customDurationRange] : undefined}
								component={ReduxControls.Input}
								{...IMPOSIUM_COMPOSITION_CONSTRAINTS.duration}
							/>
							<span>sec</span>
						</>}
					</div>
					<Field
						name={`${name}.frameRate`}
						label="Frame Rate"
						className="framerate-field cypress-imposium-framerate-field"
						isClearable={false}
						required
						component={ReduxControls.Select}
						options={IMPOSIUM_COMPOSITION_FRAMERATE_OPTIONS}
						validate={requiredNotNull}
						onMenuScrollToBottom={noop}
					/>
				</div>
			</div>
		</div>
	);
}

CompositionFields.displayName = "CompositionFields";
